import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Product } from '../../common/product';
import { CommonModule } from '@angular/common';
import { HeaderAdminComponent } from '../header-admin/header-admin.component';
import { Router, RouterModule } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import * as AdminProductsActions from '../../state/admin-products/admin-products.actions';
import {
  selectAdminProductsList,
  selectAdminProductsLoading,
  selectAdminTotalPages,
  selectAdminCurrentPage,
  selectAdminError,
} from '../../state/admin-products/admin-products.selectors';
import { AdminProductService } from '@app/services/admin-product.service';
import { GalaxyButtonDirective } from '@app/directives/galaxy-button.directive';
import { GalaxyRippleDirective } from '@app/directives/galaxy-ripple.directive';
import { AdminPageHeaderComponent } from '../admin/admin-page-header/admin-page-header.component';
import { GalaxyCardComponent } from '@app/components/ui/galaxy-card/galaxy-card.component';
import { InteractiveCrystalDirective } from '@app/directives/interactive-crystal.directive';
import { AnimateStaggerDirective } from '@app/directives/animate-stagger.directive';
import { GalaxySkeletonComponent } from '@app/components/ui/galaxy-skeleton/galaxy-skeleton.component';

@Component({
  selector: 'app-product-list',
  imports: [
    CommonModule,
    RouterModule,
    GalaxyButtonDirective,
    GalaxyRippleDirective,
    AdminPageHeaderComponent,
    GalaxyCardComponent,
    InteractiveCrystalDirective,
    AnimateStaggerDirective,
    GalaxySkeletonComponent,
  ],
  templateUrl: './product-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./product-list.component.css'],
})
export class ProductListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly adminProductService = inject(AdminProductService); // Still needed for write operations

  products$: Observable<Product[]> = this.store.select(selectAdminProductsList);
  isLoading$: Observable<boolean> = this.store.select(
    selectAdminProductsLoading,
  );
  totalPages$: Observable<number> = this.store.select(selectAdminTotalPages);
  currentPage$: Observable<number> = this.store.select(selectAdminCurrentPage);
  error$: Observable<any> = this.store.select(selectAdminError);

  pageSize: number = 10;
  private errorSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.loadProductsPage(0);
    this.errorSubscription = this.error$
      .pipe(
        tap((error) => {
          if (error) {
            console.error('Error al cargar los productos del admin:', error);
            this.notificationService.showError(
              'Error de Carga',
              'No se pudieron cargar los productos. El token podría haber expirado.',
            );
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }

  loadProductsPage(page: number): void {
    this.store.dispatch(
      AdminProductsActions.loadAdminProducts({ page, size: this.pageSize }),
    );
  }

  deleteProductById(id: number): void {
    this.notificationService
      .confirmDelete(
        '¿Quieres desactivar el producto?',
        'Esta acción lo ocultará de la tienda.',
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.adminProductService.deleteProductById(id).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                'Productos',
                'Producto desactivado correctamente.',
              );
              this.refreshCurrentPage();
            },
            error: (err: HttpErrorResponse) => {
              this.notificationService.showError(
                'Error',
                'No se pudo desactivar el producto.',
              );
              console.error('Error deactivating product:', err);
            },
          });
        }
      });
  }

  reactivateProductById(id: number): void {
    this.notificationService
      .confirm(
        '¿Quieres reactivar el producto?',
        'Esta acción hará que el producto sea visible nuevamente para los clientes.',
        'question',
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.adminProductService.reactivateProductById(id).subscribe({
            next: () => {
              this.notificationService.showSuccess(
                'Productos',
                'Producto reactivado correctamente.',
              );
              this.refreshCurrentPage();
            },
            error: (err: HttpErrorResponse) => {
              this.notificationService.showError(
                'Error',
                'No se pudo reactivar el producto.',
              );
              console.error('Error reactivating product:', err);
            },
          });
        }
      });
  }

  private refreshCurrentPage(): void {
    this.currentPage$.pipe(take(1)).subscribe((page) => {
      this.loadProductsPage(page);
    });
  }

  onPageChange(page: number): void {
    this.loadProductsPage(page);
  }

  goToPreviousPage(): void {
    this.currentPage$.pipe(take(1)).subscribe((page) => {
      if (page > 0) {
        this.loadProductsPage(page - 1);
      }
    });
  }

  goToNextPage(): void {
    this.currentPage$.pipe(take(1)).subscribe((page) => {
      this.loadProductsPage(page + 1);
    });
  }

  goToAddProductForm(): void {
    this.router.navigate(['/admin/product/addproduct']);
  }

  getPageNumbers(totalPages: number): number[] {
    if (totalPages > 0) {
      return Array(totalPages)
        .fill(0)
        .map((x, i) => i);
    }
    return [];
  }
}
